import { Zap, Shield, TrendingUp, Layers, Egg, Sprout, Sun, BadgePercent, ArrowUpRight, Baby, Wheat, FlaskConical, CircleDot, Beaker } from "lucide-react";
import { useNavigate } from "react-router-dom";
import broilerPreStarterImg from "@/assets/products/broiler-pre-starter.png";
import broilerStarterImg from "@/assets/products/broiler-starter.png";
import broilerFinisherImg from "@/assets/products/broiler-finisher.png";
import broilerConcentrateImg from "@/assets/products/broiler-concentrate.png";
import hikStarterImg from "@/assets/products/hik-starter.png";
import hikFinisherImg from "@/assets/products/hik-finisher.png";

const broilerProducts = [
  {
    id: "1", icon: Zap, name: "Broiler Pre-Starter", stage: "0–11 Days", form: "Pellet", image: broilerPreStarterImg,
    description: "High-energy pellet formula for rapid early growth, strong skeletal development, and immune building. Enriched with probiotics, digestive enzymes, and essential amino acids.",
    color: "from-primary/20 to-primary/5"
  },
  {
    id: "2", icon: TrendingUp, name: "Broiler Starter", stage: "12–25 Days", form: "Pellet", image: broilerStarterImg,
    description: "Balanced pellet feed with optimized energy-to-protein ratio for consistent weight gain. Contains antioxidants, balanced calcium-phosphorus ratio, and digestive support for optimal gut health.",
    color: "from-accent/20 to-accent/5"
  },
  {
    id: "3", icon: Shield, name: "Broiler Finisher", stage: "25+ Days", form: "Pellet", image: broilerFinisherImg,
    description: "Final-stage pellet feed optimized for superior meat texture, carcass yield (72%+ dressing percentage), and cost-effective FCR. Proven across 500+ farms in North India.",
    color: "from-primary/20 to-primary/5"
  },
  {
    id: "4", icon: Layers, name: "Broiler Concentrated Feed", stage: "25+ Days", form: "Mash", image: broilerConcentrateImg,
    description: "High-potency concentrate — mix with locally sourced grains for a complete, cost-effective feed. Ideal for small-scale farmers who want flexibility and savings on feed costs.",
    color: "from-accent/20 to-accent/5"
  },
];

const layerCrumbsProducts = [
  {
    id: "5", icon: Baby, name: "Layer Chick Crumbs", stage: "0–8 Weeks", form: "Crumble",
    description: "Nutrient-dense crumble feed for layer chicks — builds strong bones, uniform body weight, and early immune resilience for a productive laying life ahead.",
    color: "from-primary/20 to-primary/5"
  },
  {
    id: "6", icon: Sprout, name: "Layer Grower Crumbs", stage: "8–16 Weeks", form: "Crumble",
    description: "Balanced crumble feed for growing pullets. Promotes uniform flock development, strong skeletal frame, and optimal body weight gain during the critical growth phase.",
    color: "from-accent/20 to-accent/5"
  },
  {
    id: "7", icon: TrendingUp, name: "Layer Developer Crumbs", stage: "16–20 Weeks", form: "Crumble",
    description: "Developer-stage crumble feed to prepare pullets for production. Supports reproductive system maturation, body conditioning, and builds calcium reserves for shell formation.",
    color: "from-primary/20 to-primary/5"
  },
  {
    id: "8", icon: Egg, name: "Layer Pre-Lay Crumbs", stage: "20–22 Weeks", form: "Crumble",
    description: "Pre-lay transition crumble to bridge the gap between development and peak production. Boosts calcium and nutrient stores just before the onset of egg laying.",
    color: "from-accent/20 to-accent/5"
  },
];

const layerPhaseProducts = [
  {
    id: "11", icon: Sun, name: "Layer Crumbs Phase - 1", stage: "22–42 Weeks", form: "Crumble",
    description: "Peak production crumble feed with high calcium and balanced amino acids for maximum egg output, strong shell quality, and sustained laying persistence.",
    color: "from-primary/20 to-primary/5"
  },
  {
    id: "12", icon: Shield, name: "Layer Crumbs Phase - 2", stage: "42–62 Weeks", form: "Crumble",
    description: "Mid-cycle crumble feed optimized for sustained egg production and shell quality as hens mature. Maintains laying rate with adjusted energy and calcium levels.",
    color: "from-accent/20 to-accent/5"
  },
  {
    id: "13", icon: Layers, name: "Layer Crumbs Phase - 3", stage: "62+ Weeks", form: "Crumble",
    description: "Late-cycle crumble feed for older hens. Enhanced calcium and phosphorus levels to maintain shell strength and extend productive laying life.",
    color: "from-primary/20 to-primary/5"
  },
];

const layerConcentrateProducts = [
  {
    id: "14", icon: FlaskConical, name: "Layer Concentrate Phase - 1", stage: "22–42 Weeks", form: "Mash",
    description: "High-potency layer concentrate for peak production — mix with locally sourced grains for a complete, cost-effective layer feed during the most productive weeks.",
    color: "from-accent/20 to-accent/5"
  },
  {
    id: "15", icon: Beaker, name: "Layer Concentrate Phase - 2", stage: "42+ Weeks", form: "Mash",
    description: "Layer concentrate for mid-to-late cycle hens. Flexible mixing with local grains provides balanced nutrition at lower cost for extended production periods.",
    color: "from-primary/20 to-primary/5"
  },
];

const hikProducts = [
  {
    id: "9", icon: BadgePercent, name: "Broiler HIKE Starter", stage: "12–24 Days", form: "Pellet", image: hikStarterImg,
    description: "Economical starter feed with a balanced nutrient profile for steady weight gain. A cost-effective choice for farmers looking to optimize feed expenses without compromising basic growth.",
    color: "from-muted/30 to-muted/10"
  },
  {
    id: "10", icon: BadgePercent, name: "Broiler HIKE Finisher", stage: "25+ Days", form: "Pellet", image: hikFinisherImg,
    description: "Value finisher feed for the final growth phase. Delivers acceptable weight gain and carcass quality at a significantly lower cost per kg of feed.",
    color: "from-muted/30 to-muted/10"
  },
];

const ProductCard = ({ product, isLarge, navigate }: { product: { id: string; icon: any; name: string; stage: string; form: string; description: string; color: string; image?: string }; isLarge: boolean; navigate: ReturnType<typeof useNavigate> }) => {
  const Icon = product.icon;
  return (
    <div
      onClick={() => navigate(`/product/${product.id}`)}
      className={`group cursor-pointer rounded-2xl bg-gradient-to-br ${product.color} border border-border/50 
        transition-all duration-500 hover:shadow-2xl hover:-translate-y-1 overflow-hidden
        ${isLarge ? "md:row-span-1 p-10 md:p-14" : "p-8 md:p-10"}`}
    >
      {product.image && (
        <div className="flex items-center justify-center mb-6 h-52 md:h-64 mx-auto">
          <img src={product.image} alt={product.name} className="max-h-full max-w-full object-contain object-center drop-shadow-lg group-hover:scale-105 transition-transform duration-500 mx-auto" />
        </div>
      )}
      <div className="flex justify-between items-start mb-4">
        <div className="w-12 h-12 rounded-2xl bg-card flex items-center justify-center shadow-sm">
          <Icon className="h-6 w-6 text-primary" />
        </div>
        <ArrowUpRight className="h-5 w-5 text-muted-foreground/40 group-hover:text-accent group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" />
      </div>
      <div className="flex items-center gap-3 mb-2">
        <span className="text-xs uppercase tracking-[0.2em] text-accent font-body font-semibold">{product.stage}</span>
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-body bg-secondary/60 px-2 py-0.5 rounded-full">{product.form}</span>
      </div>
      <h3 className="text-2xl md:text-3xl font-bold text-foreground font-display mt-2 mb-3">{product.name}</h3>
      <p className="text-muted-foreground font-body leading-relaxed max-w-md text-sm">{product.description}</p>
    </div>
  );
};

const ProductsSection = () => {
  const navigate = useNavigate();

  return (
    <section id="products" className="py-32 bg-background relative overflow-hidden">
      <div className="absolute -left-8 top-12 text-[15rem] font-display font-bold text-foreground/[0.02] leading-none select-none pointer-events-none">
        04
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Editorial section header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-20">
          <div className="lg:col-span-5">
            <span className="text-accent font-body text-xs uppercase tracking-[0.25em] font-semibold">Our Range</span>
            <h2 className="text-5xl md:text-6xl font-bold text-foreground font-display mt-3 leading-[1.05]">
              Feed
              <br />
              <span className="italic font-normal">solutions</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 flex items-end">
            <p className="text-muted-foreground font-body text-lg leading-relaxed">
              Scientifically formulated broiler and layer feeds — each designed for a specific growth stage, rigorously tested, and proven across 500+ farms.
            </p>
          </div>
        </div>

        {/* Broiler Feeds */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-2xl font-bold text-foreground font-display">Broiler Feeds</h3>
            <span className="text-[10px] uppercase tracking-wider font-body font-semibold bg-primary/10 text-primary px-3 py-1 rounded-full">Premium</span>
          </div>
          <p className="text-muted-foreground font-body text-sm mb-8">Complete nutrition for every broiler growth stage</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {broilerProducts.map((product) => (
              <ProductCard key={product.id} product={product} isLarge={false} navigate={navigate} />
            ))}
          </div>
        </div>

        {/* Broiler HIK (Economy) */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-2xl font-bold text-foreground font-display">Broiler HIKE Feeds</h3>
            <span className="text-[10px] uppercase tracking-wider font-body font-semibold bg-accent/10 text-accent px-3 py-1 rounded-full">Economy</span>
          </div>
          <p className="text-muted-foreground font-body text-sm mb-8">Affordable nutrition for cost-conscious farmers</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {hikProducts.map((product) => (
              <ProductCard key={product.id} product={product} isLarge={false} navigate={navigate} />
            ))}
          </div>
        </div>

        {/* Layer Crumbs */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-foreground font-display mb-2">Layer Crumbs</h3>
          <p className="text-muted-foreground font-body text-sm mb-8">Complete crumble nutrition from chick to pre-lay stage</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {layerCrumbsProducts.map((product) => (
              <ProductCard key={product.id} product={product} isLarge={false} navigate={navigate} />
            ))}
          </div>
        </div>

        {/* Layer Crumbs Phase */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-foreground font-display mb-2">Layer Crumbs Phase</h3>
          <p className="text-muted-foreground font-body text-sm mb-8">Optimized for egg production, shell quality, and laying persistence</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {layerPhaseProducts.map((product) => (
              <ProductCard key={product.id} product={product} isLarge={false} navigate={navigate} />
            ))}
          </div>
        </div>

        {/* Layer Concentrate */}
        <div>
          <h3 className="text-2xl font-bold text-foreground font-display mb-2">Layer Concentrate</h3>
          <p className="text-muted-foreground font-body text-sm mb-8">Mix with local grains for cost-effective layer nutrition</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {layerConcentrateProducts.map((product) => (
              <ProductCard key={product.id} product={product} isLarge={false} navigate={navigate} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;
